import Dropzone from 'react-dropzone'

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Icon,
  Text,
  TextSpinner,
} from '@factorialco/deprecated-design-system'

import LabeledInput from 'modules/core/components/LabeledInput'
import SelectField from 'modules/core/components/SelectField'
import { useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import Result from 'modules/finance/components/Accounting/Accounts/ImportResult'
import { useAccountsImport } from 'modules/finance/hooks/accounting/useAccountsImport'
import type { LedgerAccountsLegalEntity } from 'modules/finance/pages/Accounting/AccountsPage'

export interface LedgerAccountBulkImportFormValues {
  prefillToggleValue: boolean
  legalEntityId: number
  file?: File
}

interface ImportAccountModalProps {
  form: ReturnType<typeof useForm<LedgerAccountBulkImportFormValues>>
  legalEntities: LedgerAccountsLegalEntity[]
  onDownloadTemplate: () => void
  onDropFile: (files: File[]) => void
  loading: boolean
  loadingMessage: string
  summary: ReturnType<typeof useAccountsImport>['parseSummary']
  errors:
    | ReturnType<typeof useAccountsImport>['validateParseErrors']
    | ReturnType<typeof useAccountsImport>['validateSubmitErrors']
}

export const BulkImportForm = ({
  form,
  legalEntities,
  onDownloadTemplate,
  onDropFile,
  loading,
  loadingMessage,
  summary,
  errors,
}: ImportAccountModalProps) => {
  const leganEntityOptions = useUnsafeMemo(() => {
    return legalEntities.map((le) => ({
      value: le.id,
      label: le.legalName ?? '',
    }))
  }, [legalEntities])

  return (
    <>
      {loading ? (
        <TextSpinner text={loadingMessage} />
      ) : (
        <Box gap="s24">
          <Box gap="s8">
            <Text.Normal weight="bold">
              {i18n.t('finance.accounting_accounts.import_accounts_modal.download_template')}
            </Text.Normal>
            <Text.Normal>
              {i18n.t(
                'finance.accounting_accounts.import_accounts_modal.download_template_description'
              )}
            </Text.Normal>
            <Box paddingY={'s8'}>
              <Button type="primary" iconName="download" onClick={onDownloadTemplate}>
                {i18n.t('finance.accounting_accounts.import_accounts_modal.download_button')}
              </Button>
            </Box>

            <Checkbox
              checked={form.values.prefillToggleValue}
              size="small"
              onChange={(event) =>
                event.target.checked
                  ? form.setFieldValue('prefillToggleValue', true)
                  : form.setFieldValue('prefillToggleValue', false)
              }
              label={i18n.t('finance.accounting_accounts.import_accounts_modal.prefill_data')}
            ></Checkbox>
          </Box>
          <Divider />
          <SelectField
            label={i18n.t('finance.account_ledger.new_modal.legal_entity')}
            value={form.values.legalEntityId + ''}
            onChange={(value) => form.setFieldValue('legalEntityId', Number(value))}
            options={leganEntityOptions}
            mandatory
          />

          <Box flexDirection="column" gap="s4">
            <LabeledInput
              isLegend
              label={i18n.t('finance.accounting_accounts.import_accounts_modal.drag_drop_title')}
              description={i18n.t(
                'finance.accounting_accounts.import_accounts_modal.drag_drop_message'
              )}
              mandatory={!!form.errors.file}
              error={form.errors.file}
            >
              <Dropzone
                disabled={form.values.legalEntityId === undefined}
                onDrop={onDropFile}
                multiple={false}
                accept={{
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                }}
              >
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <div {...getRootProps()}>
                    <Box
                      background={isDragActive ? 'viridian100' : 'grey000White'}
                      padding="s32"
                      cursor="pointer"
                      flexDirection="column"
                      alignItems="center"
                      borderRadius={{ all: 'abs006' }}
                      border={{
                        all: {
                          width: 's2',
                          style: 'dashed',
                          color: 'grey400',
                        },
                      }}
                    >
                      <input data-testid="dropzone-input" {...getInputProps()} />
                      <Box textAlign="center">
                        {form.values.file?.name ? (
                          <Box alignItems="center" gap="s12">
                            <Icon.Large icon="document-edit" />
                            <Text.Normal>{form.values.file.name}</Text.Normal>
                          </Box>
                        ) : (
                          <Box alignItems="center">
                            <Box paddingBottom="s6">
                              <Icon.Large icon="file-upload" />
                            </Box>
                            <Text.Normal weight="semibold">
                              {i18n.t(
                                'finance.accounting_accounts.import_accounts_modal.drag_drop_placeholder'
                              )}
                            </Text.Normal>
                            <Text.Normal>
                              {i18n.t(
                                'finance.accounting_accounts.import_accounts_modal.drag_drop_placeholder_2'
                              )}
                            </Text.Normal>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </div>
                )}
              </Dropzone>
            </LabeledInput>
          </Box>

          <Result summary={summary} errors={errors} />
        </Box>
      )}
    </>
  )
}
